/**
 * Нормативная библиотека (БЗ-14, БЗ-15, БЗ-18): загрузка документов с Диска компании и файлами из браузера,
 * отчёт загрузки, перечни АГСК и сверка, список на проверку сметному отделу и ГИП, файлы в карточке документа.
 */
import {
  AGSK_LISTS,
  type AgskImportResult,
  type AgskList,
  type AgskOverview,
  canCompanyWide,
  LIBRARY_IMPORT_STATUSES,
  LIBRARY_REVIEW_KINDS,
  LIBRARY_ROW_RESULTS,
  type LibraryCheckResult,
  type LibraryDriveStatus,
  type LibraryFile,
  type LibraryImport,
  type LibraryImportDetail,
  type LibraryReview,
  type LibraryReviewPatch,
  type LibraryRowResult,
  NORM_STATUSES,
  type NormStatus,
  REVIEW_OWNERS,
} from '@epc/contracts';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import clsx from 'clsx';
import { CloudDownload, FileUp, FolderUp, ListChecks } from 'lucide-react';
import { useRef, useState } from 'react';
import { useCrumbs } from '../../layout/crumbs';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';
import { useMe } from '../../lib/session';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Input, Select, Textarea } from '../../ui/form';
import { Page, PageHeader, Panel, TitleBlock } from '../../ui/layout';

const driveQuery = queryOptions({
  queryKey: ['norms', 'library', 'drive'],
  queryFn: () => api<LibraryDriveStatus>('/norms/library/drive'),
  refetchInterval: (q) => (q.state.data?.running ? 5000 : false),
});
const importsQuery = queryOptions({
  queryKey: ['norms', 'library', 'imports'],
  queryFn: () => api<LibraryImport[]>('/norms/library/imports'),
});
const agskQuery = queryOptions({
  queryKey: ['norms', 'agsk'],
  queryFn: () => api<AgskOverview>('/norms/agsk'),
});
const reviewsQuery = (status: 'open' | 'resolved') =>
  queryOptions({
    queryKey: ['norms', 'library', 'reviews', status],
    queryFn: () => api<LibraryReview[]>('/norms/library/reviews', { query: { status, limit: 500 } }),
  });
const importQuery = (id: string, result: string, q: string, offset: number) =>
  queryOptions({
    queryKey: ['norms', 'library', 'import', id, result, q, offset],
    queryFn: () =>
      api<LibraryImportDetail>(`/norms/library/imports/${id}`, {
        query: { result: result || undefined, q: q || undefined, offset, limit: 200 },
      }),
    refetchInterval: (query) => {
      const s = query.state.data?.import.status;
      return s === 'running' || s === 'queued' ? 4000 : false;
    },
  });

const RESULT_TONE: Record<LibraryRowResult, 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  loaded: 'success',
  duplicate: 'neutral',
  wrapper: 'info',
  archive: 'info',
  skipped: 'neutral',
  review: 'warning',
  error: 'danger',
};
const STATUS_TONE: Record<LibraryImport['status'], 'success' | 'warning' | 'danger' | 'neutral' | 'info'> = {
  queued: 'info',
  running: 'warning',
  done: 'success',
  failed: 'danger',
  cancelled: 'neutral',
};

const mb = (b: number) =>
  b >= 1024 ** 3
    ? `${(b / 1024 ** 3).toFixed(1)} ГБ`
    : b >= 1024 * 1024
      ? `${Math.round(b / 1024 / 1024)} МБ`
      : `${Math.max(1, Math.round(b / 1024))} КБ`;

function invalidateLibrary(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['norms'] });
}

/** Загрузка файлов или папки целиком из браузера (пачками по 20 файлов — предел запроса) */
function UploadFiles({ folder, onDone }: { folder: boolean; onDone: (d: LibraryImportDetail) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState<string | null>(null);
  const m = useMutation({
    mutationFn: async (files: File[]) => {
      let last: LibraryImportDetail | null = null;
      for (let i = 0; i < files.length; i += 20) {
        const batch = files.slice(i, i + 20);
        setProgress(`${Math.min(i + 20, files.length)} из ${files.length}`);
        const form = new FormData();
        form.append(
          'paths',
          JSON.stringify(
            batch.map((f) => (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name),
          ),
        );
        for (const f of batch) form.append('file', f, f.name);
        const d = await api<LibraryImportDetail>('/norms/library/upload', { form });
        // Итог — по всем пачкам вместе
        if (last) {
          for (const k of Object.keys(d.import.totals) as Array<keyof typeof d.import.totals>)
            d.import.totals[k] += last.import.totals[k];
        }
        last = d;
      }
      return last!;
    },
    onSuccess: (d) => {
      setProgress(null);
      onDone(d);
    },
    onError: (e) => {
      setProgress(null);
      toast((e as Error).message, 'danger');
    },
  });
  return (
    <>
      <input
        ref={ref}
        type="file"
        multiple
        className="hidden"
        {...(folder ? { webkitdirectory: '', directory: '' } : {})}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []).filter((f) => f.size > 0 || !folder);
          if (files.length) m.mutate(files);
          e.target.value = '';
        }}
      />
      <Button
        className="min-h-[44px] md:min-h-0"
        icon={folder ? <FolderUp className="size-4" /> : <FileUp className="size-4" />}
        loading={m.isPending}
        onClick={() => ref.current?.click()}
      >
        {progress ? `Загрузка ${progress}` : folder ? 'Загрузить папку' : 'Загрузить файлы'}
      </Button>
    </>
  );
}

function AgskUpload({ onDone }: { onDone: (r: AgskImportResult) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  const m = useMutation({
    mutationFn: (f: File) => {
      const form = new FormData();
      form.append('file', f, f.name);
      return api<AgskImportResult>('/norms/agsk/import', { form });
    },
    onSuccess: onDone,
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  return (
    <>
      <input
        ref={ref}
        type="file"
        accept=".pdf,.txt"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) m.mutate(f);
          e.target.value = '';
        }}
      />
      <Button
        className="min-h-[44px] md:min-h-0"
        size="sm"
        loading={m.isPending}
        onClick={() => ref.current?.click()}
      >
        Загрузить перечень
      </Button>
    </>
  );
}

function ImportRow({ imp }: { imp: LibraryImport }) {
  const t = imp.totals;
  return (
    <Link
      to="/norms/library/imports/$importId"
      params={{ importId: imp.id }}
      className="flex min-h-[44px] flex-col gap-1 px-5 py-3 text-[13px] hover:bg-tile/60 md:flex-row md:items-center md:gap-4"
    >
      <span className="w-[150px] shrink-0 text-ink-2">{dateTime(imp.createdAt)}</span>
      <span className="min-w-0 flex-1 font-medium text-ink [overflow-wrap:anywhere]">
        {imp.source === 'drive'
          ? `Диск: ${imp.folderName ?? imp.folderId ?? ''}`
          : imp.source === 'agsk'
            ? imp.folderName
            : 'Файлы из браузера'}
        {imp.progress && imp.status === 'running' ? (
          <span className="block text-ink-2">{imp.progress}</span>
        ) : null}
        {imp.error ? <span className="block text-danger">{imp.error}</span> : null}
      </span>
      <span className="text-ink-2">
        {t.files} файлов · загружено {t.loaded}
        {t.duplicate ? ` · копий ${t.duplicate}` : ''}
        {t.review ? ` · на проверку ${t.review}` : ''}
        {t.error ? ` · ошибок ${t.error}` : ''}
      </span>
      <Badge tone={STATUS_TONE[imp.status]}>{LIBRARY_IMPORT_STATUSES[imp.status]}</Badge>
    </Link>
  );
}

/** Папка Диска компании: задаёт владелец компании или администратор портала */
function DriveFolder({ status }: { status: LibraryDriveStatus }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(status.folderId ?? '');
  const m = useMutation({
    mutationFn: (folderId: string | null) =>
      api('/norms/library/drive', { method: 'PUT', body: { folderId } }),
    onSuccess: () => {
      toast('Папка Диска сохранена');
      invalidateLibrary(qc);
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  if (!status.canSetFolder) {
    return (
      <p>
        Папка Диска компании:{' '}
        {status.folderId ? (
          <span className="font-medium text-ink">{status.folderId}</span>
        ) : (
          'не задана — задаёт владелец компании'
        )}
      </p>
    );
  }
  return (
    <form
      className="flex flex-col gap-2 md:flex-row md:items-end"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate(value.trim() || null);
      }}
    >
      <Field
        label="Папка Диска компании"
        htmlFor="drive-folder"
        hint="Идентификатор папки из адреса: drive.google.com/drive/folders/<идентификатор>"
      >
        <Input
          id="drive-folder"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="1jodtB-…"
          className="min-h-[44px] md:min-h-0"
        />
      </Field>
      <Button
        type="submit"
        className="min-h-[44px] md:min-h-0"
        loading={m.isPending}
        disabled={(status.folderId ?? '') === value.trim()}
      >
        Сохранить
      </Button>
    </form>
  );
}

/** Блок на странице нормативной базы */
export function LibraryPanel() {
  const me = useMe();
  const qc = useQueryClient();
  const manage = canCompanyWide(me.access, 'norms.manage');
  const drive = useQuery(driveQuery);
  const imports = useQuery(importsQuery);
  const agsk = useQuery(agskQuery);
  const reviews = useQuery(reviewsQuery('open'));
  const start = useMutation({
    mutationFn: () => api<{ id: string }>('/norms/library/drive', { method: 'POST', body: {} }),
    onSuccess: () => {
      toast('Загрузка с Диска началась: ход виден в списке загрузок');
      invalidateLibrary(qc);
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api(`/norms/library/imports/${id}/cancel`, { method: 'POST', body: {} }),
    onSuccess: () => invalidateLibrary(qc),
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const check = useMutation({
    mutationFn: () => api<LibraryCheckResult>('/norms/library/check', { method: 'POST', body: {} }),
    onSuccess: (r) => {
      toast(
        `Сверено ${r.checked} документов: подтверждено ${r.confirmed}, заменено ${r.replaced}, на проверку ${r.reviews}`,
      );
      invalidateLibrary(qc);
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  const onUploaded = (d: LibraryImportDetail) => {
    invalidateLibrary(qc);
    const t = d.import.totals;
    toast(
      `Загружено ${t.loaded} из ${t.files} файлов${t.duplicate ? `, копий ${t.duplicate}` : ''}${t.review ? `, на проверку ${t.review}` : ''}`,
    );
  };
  const open = reviews.data?.length ?? 0;
  const running = drive.data?.running ?? null;
  return (
    <>
      <Panel
        title="Загрузка документов"
        meta="с Диска компании только на чтение и файлами из браузера · БЗ-14"
        bodyClassName="p-0"
        actions={
          manage ? (
            <div className="flex flex-wrap gap-2">
              {drive.data?.configured ? (
                running ? (
                  <Button
                    className="min-h-[44px] md:min-h-0"
                    variant="danger"
                    loading={cancel.isPending}
                    onClick={() => cancel.mutate(running.id)}
                  >
                    Остановить загрузку
                  </Button>
                ) : (
                  <Button
                    className="min-h-[44px] md:min-h-0"
                    variant="primary"
                    icon={<CloudDownload className="size-4" />}
                    loading={start.isPending}
                    disabled={!drive.data.folderId}
                    onClick={() => start.mutate()}
                  >
                    Загрузить с Диска
                  </Button>
                )
              ) : null}
              <UploadFiles folder onDone={onUploaded} />
              <UploadFiles folder={false} onDone={onUploaded} />
            </div>
          ) : null
        }
      >
        {drive.data && !drive.data.configured ? (
          <p className="px-5 py-3 text-[13px] text-ink-2">
            Загрузка с Диска не настроена: администратор портала задаёт на сервере ключ сервисного аккаунта
            Google, а папку «Сметы» компания открывает этому аккаунту как читателю. До этого документы
            загружаются из браузера — папкой или файлами.
          </p>
        ) : drive.data ? (
          <div className="flex flex-col gap-2 px-5 py-3 text-[13px] text-ink-2">
            {drive.data.accountEmail ? (
              <p>
                Диск читает сервисный аккаунт{' '}
                <span className="font-medium text-ink">{drive.data.accountEmail}</span> — папка должна быть
                открыта ему как читателю. Повторная загрузка той же папки докачивает только новые файлы.
              </p>
            ) : null}
            <DriveFolder status={drive.data} />
          </div>
        ) : null}
        {imports.isPending ? (
          <Spinner />
        ) : imports.isError ? (
          <ErrorBox error={imports.error} />
        ) : imports.data.length ? (
          <ul className="divide-y divide-line">
            {imports.data.slice(0, 8).map((imp) => (
              <li key={imp.id}>
                <ImportRow imp={imp} />
              </li>
            ))}
          </ul>
        ) : (
          <div className="px-5 py-4">
            <EmptyState title="Загрузок ещё не было">
              Одна копия каждого файла по контрольной сумме, HTML-обёртки заменяются исходными файлами, архивы
              распаковываются, обозначение и редакция берутся с титула. Отчёт — по каждому файлу.
            </EmptyState>
          </div>
        )}
      </Panel>

      <Panel
        title="Перечни АГСК и сверка статусов"
        meta="БЗ-15"
        bodyClassName="p-0"
        actions={
          manage ? (
            <div className="flex flex-wrap gap-2">
              <AgskUpload
                onDone={(r) => {
                  invalidateLibrary(qc);
                  toast(
                    `${AGSK_LISTS[r.list].split(' — ')[0]}${r.listDate ? ` на ${r.listDate}` : ''}: ${r.entries} записей; сверено ${r.check.checked}, подтверждено ${r.check.confirmed}, на проверку ${r.check.reviews}`,
                  );
                }}
              />
              <Button
                className="min-h-[44px] md:min-h-0"
                size="sm"
                icon={<ListChecks className="size-4" />}
                loading={check.isPending}
                onClick={() => check.mutate()}
              >
                Сверить библиотеку
              </Button>
            </div>
          ) : null
        }
      >
        {agsk.isPending ? (
          <Spinner />
        ) : agsk.isError ? (
          <ErrorBox error={agsk.error} />
        ) : (
          <ul className="divide-y divide-line">
            {agsk.data.lists.map((l) => (
              <li
                key={l.list}
                className="flex min-h-[44px] flex-col gap-1 px-5 py-3 text-[13px] md:flex-row md:items-center md:gap-4"
              >
                <span className="min-w-0 flex-1 font-medium text-ink">{l.title}</span>
                {l.entries ? (
                  <span className="text-ink-2">
                    {l.entries} записей{l.listDate ? ` · на ${l.listDate}` : ''}
                    {l.loadedAt ? ` · загружен ${dateTime(l.loadedAt)}` : ''}
                  </span>
                ) : (
                  <Badge tone="warning">не загружен</Badge>
                )}
              </li>
            ))}
          </ul>
        )}
      </Panel>

      <Panel
        title="На проверку сметному отделу и ГИП"
        meta="расхождения с перечнями, серии с одним номером, титул против имени файла · БЗ-15, БЗ-18"
        actions={
          <Link
            to="/norms/library/reviews"
            className="inline-flex min-h-[44px] items-center text-[13px] font-medium text-accent-text hover:underline md:min-h-0"
          >
            Открыть список
          </Link>
        }
      >
        {reviews.isPending ? (
          <Spinner />
        ) : reviews.isError ? (
          <ErrorBox error={reviews.error} />
        ) : open ? (
          <p className="text-[13px] text-ink-2">
            Открытых записей: <span className="font-medium text-ink">{open}</span> — сметному отделу{' '}
            {reviews.data.filter((r) => r.owner === 'estimators').length}, ГИП{' '}
            {reviews.data.filter((r) => r.owner === 'gip').length}.
          </p>
        ) : (
          <p className="text-[13px] text-ink-2">Открытых записей нет.</p>
        )}
      </Panel>
    </>
  );
}

/** Отчёт загрузки: итоги и строка по каждому файлу */
export function LibraryImportPage() {
  const { importId } = useParams({ strict: false }) as { importId: string };
  const me = useMe();
  const qc = useQueryClient();
  const manage = canCompanyWide(me.access, 'norms.manage');
  const [result, setResult] = useState('');
  const [typed, setTyped] = useState('');
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const d = useQuery(importQuery(importId, result, q, offset));
  useCrumbs(
    d.data
      ? [{ label: 'Загрузка документов', to: '/norms' }, { label: dateTime(d.data.import.createdAt) }]
      : null,
    d.error,
  );
  const cancel = useMutation({
    mutationFn: () => api(`/norms/library/imports/${importId}/cancel`, { method: 'POST', body: {} }),
    onSuccess: () => invalidateLibrary(qc),
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  if (d.isPending) return <Spinner />;
  if (d.isError) return <ErrorBox error={d.error} />;
  const imp = d.data.import;
  const t = imp.totals;
  const active = imp.status === 'running' || imp.status === 'queued';
  return (
    <Page>
      <PageHeader
        title={
          imp.source === 'drive'
            ? `Загрузка с Диска: ${imp.folderName ?? ''}`
            : imp.source === 'agsk'
              ? `Перечень: ${imp.folderName ?? ''}`
              : 'Загрузка файлов'
        }
        subtitle={`${dateTime(imp.createdAt)}${imp.createdBy ? ` · ${imp.createdBy}` : ''}${imp.finishedAt ? ` · закончена ${dateTime(imp.finishedAt)}` : ''}`}
        actions={
          manage && active ? (
            <Button
              className="min-h-[44px] md:min-h-0"
              variant="danger"
              loading={cancel.isPending}
              onClick={() => cancel.mutate()}
            >
              Остановить
            </Button>
          ) : null
        }
      >
        <span className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-ink-2">
          <Badge tone={STATUS_TONE[imp.status]}>{LIBRARY_IMPORT_STATUSES[imp.status]}</Badge>
          {imp.progress && active ? <span>· {imp.progress}</span> : null}
          {imp.error ? <span className="text-danger">· {imp.error}</span> : null}
        </span>
      </PageHeader>
      <div className="flex flex-col gap-6">
        <TitleBlock
          items={[
            { label: 'Файлов', value: t.files, note: mb(t.bytes) },
            { label: 'Загружено', value: t.loaded, note: `карточек документов: ${t.releases}` },
            { label: 'Копий', value: t.duplicate, note: 'одна копия по контрольной сумме' },
            { label: 'Обёрток и архивов', value: t.wrapper + t.archive },
            { label: 'На проверку', value: t.review, tone: t.review ? 'danger' : 'default' },
            { label: 'Пропущено и ошибок', value: t.skipped + t.error, tone: t.error ? 'danger' : 'default' },
          ]}
        />
        <Panel
          title="Файлы"
          meta={`${d.data.total}`}
          bodyClassName="p-0"
          actions={
            <div className="flex flex-wrap gap-2">
              <Select
                value={result}
                onChange={(e) => {
                  setResult(e.target.value);
                  setOffset(0);
                }}
                aria-label="Отбор по итогу"
                className="min-h-[44px] md:min-h-0"
              >
                <option value="">Все итоги</option>
                {(Object.keys(LIBRARY_ROW_RESULTS) as LibraryRowResult[]).map((k) => (
                  <option key={k} value={k}>
                    {LIBRARY_ROW_RESULTS[k]}
                  </option>
                ))}
              </Select>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                onBlur={() => {
                  setQ(typed);
                  setOffset(0);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setQ(typed);
                    setOffset(0);
                  }
                }}
                placeholder="Путь или сообщение"
                aria-label="Поиск по строкам отчёта"
                className="min-h-[44px] rounded-md border border-line-strong bg-surface px-3 text-[13px] md:min-h-0 md:h-8"
              />
            </div>
          }
        >
          {d.data.rows.length ? (
            <ul className="divide-y divide-line">
              {d.data.rows.map((r) => (
                <li
                  key={r.id}
                  className="flex flex-col gap-1 px-5 py-3 text-[13px] md:flex-row md:items-start md:gap-4"
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-medium text-ink [overflow-wrap:anywhere]">{r.path}</span>
                    {r.message ? (
                      <span className="block text-ink-2 [overflow-wrap:anywhere]">{r.message}</span>
                    ) : null}
                    {r.duplicateOf ? (
                      <span className="block text-ink-2 [overflow-wrap:anywhere]">
                        первая копия: {r.duplicateOf}
                      </span>
                    ) : null}
                    {r.detected.code ? (
                      <span className="block text-ink-2">
                        титул: {r.detected.code}
                        {r.detected.issue ? `, ${r.detected.issue}` : ''}
                        {r.detected.orderNo ? `, приказ № ${r.detected.orderNo}` : ''}
                        {r.detected.effectiveFrom ? `, с ${r.detected.effectiveFrom}` : ''}
                        {r.detected.region ? `, ${r.detected.region}` : ''}
                      </span>
                    ) : null}
                  </span>
                  <span className="flex shrink-0 flex-wrap items-center gap-2">
                    {r.sizeBytes !== null ? (
                      <span className="text-ink-2">
                        {r.sizeBytes >= 1024 * 1024
                          ? mb(r.sizeBytes)
                          : `${Math.max(1, Math.round(r.sizeBytes / 1024))} КБ`}
                      </span>
                    ) : null}
                    {r.releaseId ? (
                      <Link
                        to="/norms/releases/$releaseId"
                        params={{ releaseId: r.releaseId }}
                        className="inline-flex min-h-[44px] items-center font-medium text-accent-text hover:underline md:min-h-0"
                      >
                        {r.releaseCode ?? 'карточка'}
                      </Link>
                    ) : null}
                    <Badge tone={RESULT_TONE[r.result]}>{LIBRARY_ROW_RESULTS[r.result]}</Badge>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="px-5 py-4">
              <EmptyState title={active ? 'Файлы ещё обрабатываются' : 'Строк нет'} />
            </div>
          )}
          {d.data.total > 200 ? (
            <div className="flex items-center justify-between gap-2 border-t border-line px-5 py-3 text-[13px]">
              <Button
                size="sm"
                className="min-h-[44px] md:min-h-0"
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - 200))}
              >
                Назад
              </Button>
              <span className="text-ink-2">
                {offset + 1}–{Math.min(offset + 200, d.data.total)} из {d.data.total}
              </span>
              <Button
                size="sm"
                disabled={offset + 200 >= d.data.total}
                onClick={() => setOffset(offset + 200)}
              >
                Дальше
              </Button>
            </div>
          ) : null}
        </Panel>
      </div>
    </Page>
  );
}

function ResolveDialog({ r, onClose }: { r: LibraryReview; onClose: () => void }) {
  const qc = useQueryClient();
  const [resolution, setResolution] = useState('');
  const [status, setStatus] = useState<'' | NormStatus>('');
  const m = useMutation({
    mutationFn: () => {
      const body: LibraryReviewPatch = {
        status: 'resolved',
        resolution,
        ...(status ? { releaseStatus: status } : {}),
      };
      return api(`/norms/library/reviews/${r.id}`, { method: 'PATCH', body });
    },
    onSuccess: () => {
      invalidateLibrary(qc);
      toast('Решение записано');
      onClose();
    },
    onError: (e) => toast((e as Error).message, 'danger'),
  });
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={LIBRARY_REVIEW_KINDS[r.kind]}
      footer={
        <>
          <Button className="min-h-[44px]" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className="min-h-[44px]"
            variant="primary"
            loading={m.isPending}
            disabled={!resolution.trim() && !status}
            onClick={() => m.mutate()}
          >
            Решено
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-ink-2 [overflow-wrap:anywhere]">{r.message}</p>
      <Field label="Что решено" htmlFor="rv-res">
        <Textarea
          id="rv-res"
          rows={3}
          value={resolution}
          onChange={(e) => setResolution(e.target.value)}
          placeholder="Сверено с приказом № …, действует"
        />
      </Field>
      {r.releaseId ? (
        <Field
          label="Статус документа по решению"
          htmlFor="rv-status"
          hint="Источник статуса — заметка сметного отдела (БЗ-13)"
        >
          <Select
            id="rv-status"
            className="min-h-[44px] md:min-h-0"
            value={status}
            onChange={(e) => setStatus(e.target.value as '' | NormStatus)}
          >
            <option value="">Не менять</option>
            {(Object.keys(NORM_STATUSES) as NormStatus[]).map((s) => (
              <option key={s} value={s}>
                {NORM_STATUSES[s]}
              </option>
            ))}
          </Select>
        </Field>
      ) : null}
    </Dialog>
  );
}

/** Список на проверку (БЗ-15, БЗ-18) */
export function LibraryReviewsPage() {
  const me = useMe();
  const manage = canCompanyWide(me.access, 'norms.manage');
  const [status, setStatus] = useState<'open' | 'resolved'>('open');
  const [owner, setOwner] = useState<'' | 'estimators' | 'gip'>('');
  const [resolve, setResolve] = useState<LibraryReview | null>(null);
  const rv = useQuery(reviewsQuery(status));
  useCrumbs([{ label: 'На проверку' }]);
  const rows = (rv.data ?? []).filter((r) => !owner || r.owner === owner);
  return (
    <Page>
      <PageHeader
        title="Нормативная библиотека: на проверку"
        subtitle="Расхождения с перечнями АГСК, серии с одним номером и разными годами, титул против имени файла, обёртки и архивы, недостающие документы"
      >
        <div className="mt-3 flex flex-wrap gap-2">
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value as 'open' | 'resolved')}
            aria-label="Статус записей"
            className="min-h-[44px] md:min-h-0"
          >
            <option value="open">Ждут решения</option>
            <option value="resolved">Решённые</option>
          </Select>
          <Select
            value={owner}
            onChange={(e) => setOwner(e.target.value as '' | 'estimators' | 'gip')}
            aria-label="Ответственный"
            className="min-h-[44px] md:min-h-0"
          >
            <option value="">Все ответственные</option>
            <option value="estimators">Сметный отдел</option>
            <option value="gip">ГИП</option>
          </Select>
        </div>
      </PageHeader>
      {rv.isPending ? (
        <Spinner />
      ) : rv.isError ? (
        <ErrorBox error={rv.error} />
      ) : rows.length ? (
        <Panel bodyClassName="p-0" meta={`${rows.length}`} title="Записи">
          <ul className="divide-y divide-line">
            {rows.map((r) => (
              <li
                key={r.id}
                className="flex flex-col gap-2 px-5 py-3 text-[13px] md:flex-row md:items-start md:gap-4"
              >
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-2">
                    <Badge tone={r.kind === 'missing' ? 'danger' : 'warning'}>
                      {LIBRARY_REVIEW_KINDS[r.kind]}
                    </Badge>
                    <Badge>{REVIEW_OWNERS[r.owner]}</Badge>
                    {r.releaseId ? (
                      <Link
                        to="/norms/releases/$releaseId"
                        params={{ releaseId: r.releaseId }}
                        className="inline-flex min-h-[44px] items-center font-medium text-accent-text hover:underline md:min-h-0"
                      >
                        {r.releaseCode ?? r.code}
                      </Link>
                    ) : r.code ? (
                      <span className="font-medium text-ink">{r.code}</span>
                    ) : null}
                  </span>
                  <span className="mt-1 block text-ink-2 [overflow-wrap:anywhere]">{r.message}</span>
                  {r.resolution ? (
                    <span className="mt-1 block text-ink [overflow-wrap:anywhere]">
                      Решено{r.resolvedBy ? ` (${r.resolvedBy})` : ''}
                      {r.resolvedAt ? ` ${dateTime(r.resolvedAt)}` : ''}: {r.resolution}
                    </span>
                  ) : null}
                  <span className="mt-1 block text-ink-3">{dateTime(r.createdAt)}</span>
                </span>
                {manage && r.status === 'open' ? (
                  <Button
                    className={clsx('min-h-[44px] shrink-0 md:min-h-0')}
                    size="sm"
                    onClick={() => setResolve(r)}
                  >
                    Решить
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        </Panel>
      ) : (
        <EmptyState title={status === 'open' ? 'Записей на проверку нет' : 'Решённых записей нет'}>
          Записи появляются после загрузки документов и сверки с перечнями АГСК.
        </EmptyState>
      )}
      {resolve ? <ResolveDialog r={resolve} onClose={() => setResolve(null)} /> : null}
    </Page>
  );
}

/** Файлы документа в карточке выпуска */
export function ReleaseFilesPanel({ releaseId }: { releaseId: string }) {
  const f = useQuery({
    queryKey: ['norms', 'release', releaseId, 'files'],
    queryFn: () => api<LibraryFile[]>(`/norms/releases/${releaseId}/files`),
  });
  const reviews = useQuery({
    queryKey: ['norms', 'library', 'reviews', 'release', releaseId],
    queryFn: () => api<LibraryReview[]>('/norms/library/reviews', { query: { status: 'open', releaseId } }),
  });
  if (f.isPending) return <Spinner />;
  if (f.isError) return <ErrorBox error={f.error} />;
  if (!f.data.length && !reviews.data?.length) return null;
  return (
    <Panel title="Файлы документа" meta={`${f.data.length} · БЗ-12, БЗ-14`} bodyClassName="p-0">
      {reviews.data?.length ? (
        <ul className="divide-y divide-line border-b border-line bg-warning-soft/40">
          {reviews.data.map((r) => (
            <li key={r.id} className="px-5 py-2 text-[13px] text-ink [overflow-wrap:anywhere]">
              <Badge tone="warning">{LIBRARY_REVIEW_KINDS[r.kind]}</Badge> {r.message}
            </li>
          ))}
        </ul>
      ) : null}
      <ul className="divide-y divide-line">
        {f.data.map((x) => (
          <li
            key={x.id}
            className="flex flex-col gap-1 px-5 py-3 text-[13px] md:flex-row md:items-center md:gap-4"
          >
            <a
              href={`/api/v1/norms/releases/${releaseId}/files/${x.id}/download`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex min-h-[44px] items-center font-medium text-accent-text hover:underline md:min-h-0 [overflow-wrap:anywhere]"
            >
              {x.name}
            </a>
            <span className="min-w-0 flex-1 text-ink-2 [overflow-wrap:anywhere]">{x.path}</span>
            <span className="shrink-0 text-ink-2">
              {x.languages.map((l) => (l === 'kk' ? 'каз' : 'рус')).join(', ')}
              {x.languages.length ? ' · ' : ''}
              {x.sizeBytes >= 1024 * 1024
                ? mb(x.sizeBytes)
                : `${Math.max(1, Math.round(x.sizeBytes / 1024))} КБ`}
            </span>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

export type { AgskList };
